'use strict';

import { CoreRepository } from './CoreRepository';
import type { CalendarEventType } from '../Entities/CalendarEvent';
import { CalendarEvent } from '../Entities/CalendarEvent';
import { CalendarEventCollection } from '../Collections/CalendarEventCollection';
import type { User } from '../Entities/User/User';
import type { Ind } from '../Entities/Ind/Ind';
import { Utilities } from '../Utilities/Utilities';
import type { Operator } from '../Model/Operator';

/**
 * Complex type to be used in the SDK
 */
export interface CalendarEventSchema {
  id?: number;
  name?: string;
  inds?: Array<Ind>;
  description?: string;
  location?: string;
  type?: CalendarEventType;
  vendorID?: string;
  dateTimeStart?: Date;
  dateTimeEnd?: Date;
  isAllDayEvent?: boolean;
  user?: User;
  creator?: Operator;
}

/**
 * Simple type for moving data between the SDK and the API
 */
export interface CalendarEventPayload {
  id?: number;
  name?: string;
  inds?: Array<number>;
  description?: string;
  location?: string;
  type?: string;
  vendorID?: string;
  dateTimeStart?: string;
  dateTimeEnd?: string;
  isAllDayEvent?: boolean;
  user?: number;
  creator?: Operator;
}

/**
 * Format when returning a list of CalendarEvent entities from the API
 */
export interface CalendarEventCollectionPayload {
  limit?: number;
  total?: number;
  offset?: number;
  data?: Array<CalendarEventPayload>;
}

/**
 * Repository for CRUD operations within the context of a CalendarEvent type
 *
 * @author Matt Lowden <matt.lowden@medicareprocrm.com>
 */
export class CalendarEventRepository extends CoreRepository {
  /**
   * Takes data supplied in the CalendarEventSchema format and converts it to a simplified version
   * for use with the API
   *
   * @param {CalendarEventSchema} data Data to be converted to a simplified format for usage with the API
   * @returns {CalendarEventPayload} Simplified data structure for use with our API
   */
  protected processData(data: CalendarEventSchema): CalendarEventPayload {
    const payload: CalendarEventPayload = {};

    if (data.hasOwnProperty('name')) {
      payload.name = data.name;
    }

    if (data.inds) {
      const inds: Array<number> = [];
      data.inds.forEach((ind) => {
        inds.push(ind.getIndId());
      });
      payload.inds = inds;
    }

    if (data.hasOwnProperty('type')) {
      payload.type = data.type;
    }

    if (data.hasOwnProperty('vendorID')) {
      payload.vendorID = data.vendorID;
    }

    if (data.hasOwnProperty('description')) {
      payload.description = data.description;
    }

    if (data.hasOwnProperty('location')) {
      payload.location = data.location;
    }

    if (data.hasOwnProperty('dateTimeStart')) {
      payload.dateTimeStart = data.dateTimeStart
        ? Utilities.dateTimeToDateTimeString(data.dateTimeStart)
        : null;
    }

    if (data.hasOwnProperty('dateTimeEnd')) {
      payload.dateTimeEnd = data.dateTimeEnd
        ? Utilities.dateTimeToDateTimeString(data.dateTimeEnd)
        : null;
    }

    if (data.hasOwnProperty('isAllDayEvent')) {
      payload.isAllDayEvent = data.isAllDayEvent;
    }

    if (data.hasOwnProperty('creator')) {
      payload.creator = data.creator;
    }

    return payload;
  }

  /**
   * Gets a list of calendar events
   *
   * @param {number} userId ID of whose events are being requested
   * @param {{}} options Object containing additional query parameters
   * @returns {Promise<CalendarEventCollection>} A promise containing the requested CalendarEventCollection.
   */
  public getCalendarEvents(userId: number, options = {}): Promise<CalendarEventCollection> {
    return this.webAPI.getCalendarEvents(userId, options).then((response) => {
      const collection = new CalendarEventCollection();

      response.data.forEach((collectionItemData) => {
        const collectionItem = new CalendarEvent();
        collectionItem.hydrate(collectionItemData);

        collection.addItem(collectionItem);
      });

      collection.setOffset(response.offset);
      collection.setLimit(response.limit);
      collection.setTotal(response.total);

      return collection;
    });
  }

  /**
   * Gets a list of calendar events between dates
   *
   * @param {number} userId ID of whose events are being requested
   * @param {{}} options Object containing additional query parameters
   * @returns {Promise<CalendarEventCollection>} A promise containing the requested CalendarEventCollection.
   */
  public getCalendarEventsBetweenDates(
    userId: number,
    dateTimeStart: string,
    dateTimeEnd: string,
    options: {} = null
  ): Promise<CalendarEventCollection> {
    return this.webAPI
      .getCalendarEventsBetweenDates(userId, dateTimeStart, dateTimeEnd, options)
      .then((response) => {
        const collection = new CalendarEventCollection();

        response.data.forEach((collectionItemData) => {
          const collectionItem = new CalendarEvent();
          collectionItem.hydrate(collectionItemData);

          collection.addItem(collectionItem);
        });

        collection.setOffset(response.offset);
        collection.setLimit(response.limit);
        collection.setTotal(response.total);

        return collection;
      });
  }

  /**
   * Gets a list of calendar events between dates
   *
   * @param {number} userId ID of whose events are being requested
   * @param {{}} options Object containing additional query parameters
   * @returns {Promise<CalendarEventCollection>} A promise containing the requested CalendarEventCollection.
   */
  public getAgencyDownlineCalendarEventsBetweenDates(
    userId: number,
    agencyId: number,
    dateTimeStart: string,
    dateTimeEnd: string,
    options: {} = null
  ): Promise<CalendarEventCollection> {
    return this.webAPI
      .getAgencyDownlineCalendarEventsBetweenDates(
        userId,
        agencyId,
        dateTimeStart,
        dateTimeEnd,
        options
      )
      .then((response) => {
        const collection = new CalendarEventCollection();

        response.data.forEach((collectionItemData) => {
          const collectionItem = new CalendarEvent();
          collectionItem.hydrate(collectionItemData);

          collection.addItem(collectionItem);
        });

        collection.setOffset(response.offset);
        collection.setLimit(response.limit);
        collection.setTotal(response.total);

        return collection;
      });
  }

  /**
   * Gets a single calendar event
   *
   * @param {number} userId ID of User whose event is being requested
   * @param {number} calendarEventId ID of calendar event being requested
   * @param {{}} options Object containing additional query parameters
   * @returns {Promise<CalendarEvent>} Promise containing requested CalendarEvent
   * @throws {Exception} If no matching event exists or user isn't authenticated
   */
  public getCalendarEvent(
    userId: number,
    calendarEventId: number,
    options = {}
  ): Promise<CalendarEvent> {
    return this.webAPI.getCalendarEvent(userId, calendarEventId, options).then((response) => {
      const item = new CalendarEvent();
      item.hydrate(response);

      return item;
    });
  }

  /**
   * Update a single CalendarEvent
   *
   * @param {number} userId ID of User whose event is being requested
   * @param {number} calendarEventId ID of calendar event being requested
   * @param {CalendarEventSchema} data Data to update
   * @returns {Promise<CalendarEvent>} Promise containing requested CalendarEvent
   * @throws {Exception} If no matching event exists, the user isn't authenticated or suppled CalendarEventSchema is invalid
   */
  public updateCalendarEvent(
    userId: number,
    calendarEventId: number,
    data: CalendarEventSchema
  ): Promise<CalendarEvent> {
    const payload = this.processData(data);

    return this.webAPI.updateCalendarEvent(userId, calendarEventId, payload).then((response) => {
      const item = new CalendarEvent();
      item.hydrate(response);

      return item;
    });
  }

  /**
   * Create a single CalendarEvent
   *
   * @param {number} userId ID of User whose event is being requested
   * @param {CalendarEventSchema} data Data to create
   * @returns {Promise<CalendarEvent>} Promise containing requested CalendarEvent
   * @throws {Exception} The user isn't authenticated or suppled CalendarEventSchema is invalid
   */
  public createCalendarEvent(userId: number, data: CalendarEventSchema): Promise<CalendarEvent> {
    const payload = this.processData(data);

    return this.webAPI.createCalendarEvent(userId, payload).then((response) => {
      const item = new CalendarEvent();
      item.hydrate(response);

      return item;
    });
  }

  /**
   * Delete a single CalendarEvent
   *
   * @param {number} userId ID of User whose event is being deleted
   * @param {number} calendarEventId ID of calendar event being deleted
   * @returns {Promise<void>} Empty promise
   * @throws {Exception} The user isn't authenticated or supplied event doesn't exist
   */
  public deleteCalendarEvent(userId: number, calendarEventId: number): Promise<void> {
    return this.webAPI.deleteCalendarEvent(userId, calendarEventId).then((response) => {
      return response;
    });
  }

  /**
   * Gets a single calendar event
   *
   * @param {number} userId ID of User whose event is being requested
   * @param {string} vendorID ID of calendar event being requested
   * @param {{}} options Object containing additional query parameters
   * @returns {Promise<CalendarEventExternal>} Promise containing requested CalendarEventExternal
   * @throws {Exception} If no matching event exists or user isn't authenticated
   */
  public getCalendarEventExternal(
    userId: number,
    vendorID: string,
    options = {}
  ): Promise<CalendarEvent> {
    return this.webAPI.getCalendarEventExternal(userId, vendorID, options).then((response) => {
      const item = new CalendarEvent();
      item.hydrate(response);

      return item;
    });
  }

  /**
   * Update a single CalendarEventExternal
   *
   * @param {number} userId ID of User whose event is being requested
   * @param {number} vendorID ID of calendar event being requested
   * @param {CalendarEventExternalSchema} data Data to update
   * @returns {Promise<CalendarEventExternal>} Promise containing requested CalendarEventExternal
   * @throws {Exception} If no matching event exists, the user isn't authenticated or suppled CalendarEventExternalSchema is invalid
   */
  public putCalendarEventExternal(
    userId: number,
    vendorID: string,
    data: CalendarEventSchema
  ): Promise<CalendarEvent> {
    const payload = this.processData(data);

    return this.webAPI.putCalendarEventExternal(userId, vendorID, payload).then((response) => {
      const item = new CalendarEvent();
      item.hydrate(response);

      return item;
    });
  }

  /**
   * Delete a single CalendarEventExternal
   *
   * @param {number} userId ID of User whose event is being deleted
   * @param {number} vendorID ID of calendar event being deleted
   * @returns {Promise<void>} Empty promise
   * @throws {Exception} The user isn't authenticated or supplied event doesn't exist
   */
  public deleteCalendarEventExternal(userId: number, vendorID: string): Promise<void> {
    return this.webAPI.deleteCalendarEventExternal(userId, vendorID).then((response) => {
      return response;
    });
  }
}
